import type { GaitKind } from './state'

/**
 * Gen5 Dash — die Seitenspielerei, als reine Rechnung.
 *
 * WAS DAS HIER IST. Ein Endlosläufer: der Roboter läuft, der Boden rollt, es
 * kommen Hindernisse. Wer sieben Mal auf die Wortmarke klickt, findet ihn.
 *
 * WARUM ER IM RECHENKERN STEHT UND NICHT IM RENDERLOOP. Die Vorlage rechnet
 * Hitze, Akku und Treffer mitten im Zeichnen. Das ist für eine Spielerei
 * vertretbar und hier trotzdem falsch: dieses Projekt trennt Rechnung und Bild
 * überall sonst, und eine Ausnahme ist keine Trennung mehr. Ausserdem lässt sich
 * eine Balance, die niemand nachrechnen kann, auch nicht ändern — wer die
 * Hitzerate anfasst, will wissen, ob der Sprint danach noch fahrbar ist.
 *
 * DIE REGELN DES KERNS GELTEN AUCH HIER: kein `three`, kein DOM, keine Uhr, kein
 * Zufall. Die Zeit kommt als `dt` herein, der Zufall als Funktion. Derselbe
 * Zustand mit derselben Eingabe ergibt denselben Folgezustand — deshalb kann
 * dieses Spiel Tests haben.
 *
 * WAS DER KERN NICHT TUT: er läuft den Roboter nicht. Geschwindigkeit und
 * Leistung reicht ihm der Aufrufer aus der laufenden Simulation herein
 * (`DashWorld`). Das Spiel ERBT die Zahlen der Maschine, es erfindet keine —
 * genau das ist der Witz daran. Wer am Modell dreht, merkt es hier.
 */

/** Was einem im Weg steht oder einem hilft. */
export type ItemKind =
  /** Hürde. Überspringen, aus jeder Gangart möglich. */
  | 'hurd'
  /** Ölfleck. Im Gehen harmlos, sonst rutscht er aus. */
  | 'oil'
  /** Riegel, 1,6 m breit. Nur mit Sprint zu überfliegen. */
  | 'pit'
  /** Energiezelle auf Kopfhöhe. Im Sprung mitnehmen. */
  | 'cell'

export interface DashItem {
  kind: ItemKind
  /** Abstand vor dem Roboter, m. Läuft gegen null und darunter. */
  x: number
  /** Schon abgehandelt — verhindert den zweiten Treffer am selben Ding. */
  done: boolean
}

/**
 * Was der Läufer gerade tut.
 *
 * VIER ZUSTÄNDE STATT DREI FLAGS. Vorher hiess „liegt er?" eine Zahl, „ist es
 * aus?" ein boolescher Wert und „läuft überhaupt etwas?" ein zweiter — und die
 * Kombinationen, die es nicht geben darf (liegt UND ist aus), musste man sich
 * denken. Als Phase ist jede Lage eine und schliesst die anderen aus.
 */
export type DashPhase =
  /** Steht an der Startlinie und wartet. Nichts bewegt sich. */
  | 'ready'
  /** Läuft. */
  | 'run'
  /** Liegt — der Sturz läuft ab. */
  | 'fall'
  /** Liegt und wartet auf die Entscheidung: neu starten oder beenden. */
  | 'prompt'

export interface DashState {
  running: boolean
  phase: DashPhase
  over: boolean
  /** Warum es aus ist. Leer, solange gelaufen wird. */
  reason: string
  /** Gelaufene Strecke, m. */
  dist: number
  /**
   * Fortbewegungsgeschwindigkeit, m/s — die WIRKLICHE, nicht die verlangte.
   *
   * SIE STEHT IM ZUSTAND UND NICHT IN DER WELT, weil sie eine Geschichte hat.
   * Vorher wurde `world.speed` unmittelbar verrechnet: ein Druck auf die 3
   * setzte den Läufer im selben Bild von 1,3 auf 4,2 m/s, die Welt sprang
   * unter ihm weg, und die Strecke bekam einen Knick. Die verlangte
   * Geschwindigkeit kommt weiter von aussen; hier steht, was davon schon
   * erreicht ist.
   *
   * Sie treibt ALLES, was sich bewegt: Strecke, Nachschub, Hindernisse. Zwei
   * Geschwindigkeiten — eine für die Anzeige, eine für die Treffer — wären
   * eine Welt, die sich anders bewegt, als man sie sieht.
   */
  v: number
  points: number
  best: number
  /** Punkte aus Zellen — getrennt geführt, weil Meter sich nicht zurücknehmen lassen. */
  cellPoints: number
  /** Hüfttemperatur, 0 … 100 %. */
  heat: number
  /** Übertemperatur: Zwangsgang, bis `HEAT.release` unterschritten ist. */
  hot: boolean
  /** Ladezustand, 0 … 100 %. */
  battery: number
  /** Höhe über dem Boden, m. */
  y: number
  /** Steiggeschwindigkeit, m/s. */
  vy: number
  airborne: boolean
  /** Verbleibende Hüllen. */
  lives: number
  /**
   * Er liegt. Restzeit des Sturzes, s. 0 heisst: er läuft.
   *
   * EIN TREFFER IST KEIN KRATZER, SONDERN EIN STURZ. Vorher kostete ein
   * Hindernis eine Hülle und der Lauf ging weiter — man rannte durch eine
   * Hürde hindurch und merkte es nur an einer Zahl. Ein Roboter, der eine
   * Hürde reisst, fällt hin; und wer hinfällt, fängt von vorn an.
   */
  falling: number
  items: DashItem[]
  /** Reststrecke bis zum nächsten Hindernis, m. */
  nextIn: number
  /** Die letzte Meldung für die Anzeige. */
  message: string
}

/**
 * Was die laufende Simulation beisteuert.
 *
 * Alles Gemessene kommt von aussen herein. Der Kern kennt weder Gangarten noch
 * Antriebe — er kennt eine Geschwindigkeit und eine Leistung.
 */
export interface DashWorld {
  /** Fortbewegungsgeschwindigkeit der laufenden Gangart, m/s. */
  speed: number
  /** Mittlere Antriebsleistung der laufenden Gangart, W. */
  power: number
  /** Akkuinhalt, J. Aus der Stückliste, nicht geraten. */
  capacity: number
  /** Die laufende Gangart — sie bestimmt die Hitze. */
  gait: GaitKind
}

export interface DashInput {
  /** In diesem Schritt gesprungen. */
  jump: boolean
  /** Losgelaufen — beendet das Warten an der Startlinie. */
  start?: boolean
  /** Gleichverteilt in [0,1). Hereingereicht, damit der Kern reproduzierbar bleibt. */
  random: () => number
}

/**
 * Wie schnell die Hüfte warm wird, Prozentpunkte je Sekunde.
 *
 * DAS IST DIE BALANCE DES SPIELS und der Grund, warum sie hier steht statt in
 * einer Schleife. Sprint heizt viermal so schnell, wie Gehen kühlt: rund vier
 * Sekunden Sprint kosten dreizehn Sekunden Gehen. Wer die Zahlen ändert, ändert
 * das Spiel — und die Tests sagen, in welche Richtung.
 */
export const HEAT = {
  sprint: 27,
  run: 6.5,
  /* Negativ: Gehen kühlt. Die einzige Gangart, die das tut. */
  walk: -19,
  /** Ab hier Zwangsgang. */
  limit: 100,
  /** Bis hierher muss sie fallen, bevor es wieder freigegeben wird. */
  release: 38,
} as const

/** Sprungphysik. Flugzeit rund 0,54 s, Scheitel 0,49 m. */
export const JUMP = { speed: 3.62, gravity: 13.4 } as const

/**
 * Wie schnell die Fortbewegung der verlangten Geschwindigkeit folgt, m/s².
 *
 * KEINE MASCHINE SPRINGT VON GEHEN AUF SPRINT. Der Wechsel von 1,3 auf
 * 4,2 m/s braucht damit rund eine halbe Sekunde — dieselbe Grössenordnung wie
 * der Übergang der Haltung in `blend.ts`, und das ist Absicht: liefe die Welt
 * schneller um als die Beine, sähe man eine Figur im Gehschritt über einen
 * Boden rasen.
 *
 * Sie gilt in beide Richtungen. Bremsen ist derselbe Vorgang wie Beschleunigen,
 * nur mit umgekehrtem Vorzeichen — und ein Läufer, der sofort langsam ist, ist
 * derselbe Fehler wie einer, der sofort schnell ist.
 */
export const ACCEL = 6

/**
 * Zeitraffer für den Akku.
 *
 * Ein Pack, das für drei Stunden Gehen ausgelegt ist, wäre in Echtzeit drei
 * Stunden lang voll — als Anzeige eine Konstante und als Spielelement wertlos.
 * Neunzig Sekunden Spielzeit stehen für eine Stunde Betrieb.
 */
export const TIME_LAPSE = 90

/** Halbe Breite je Hindernis, m — die Zone, in der es zählt. */
export const HALF: Record<ItemKind, number> = {
  hurd: 0.16,
  oil: 0.8,
  pit: 0.86,
  cell: 0.34,
}

/**
 * Wie hoch man sein muss, um darüber zu kommen, m.
 *
 * Der Riegel ist 1,6 m breit: im Laufen reicht die Flugweite nicht, egal wie
 * hoch man springt. Die Höhe allein entscheidet trotzdem, weil die Flugweite
 * über die Geschwindigkeit ohnehin an der Gangart hängt.
 */
export const CLEAR: Record<Exclude<ItemKind, 'oil'>, number> = {
  hurd: 0.33,
  pit: 0.28,
  /** Die Zelle hängt oben — man muss sie TREFFEN, nicht meiden. */
  cell: 0.28,
}

/** Über dieser Geschwindigkeit rutscht Öl. Gehen liegt darunter. */
export const OIL_SPEED = 1.7
/** Und unter dieser Höhe: wer springt, kommt drüber. */
export const OIL_CLEAR = 0.2

export const CELL_POINTS = 25
/** Was eine Zelle nachlädt, Prozentpunkte. */
export const CELL_CHARGE = 14

/** So viele Stürze hält der Lauf aus. */
export const LIVES = 3

/**
 * Wie lange er liegt, s.
 *
 * Lang genug, dass man den Sturz sieht und die Ursache begreift; kurz genug,
 * dass es nicht zur Strafe wird. Wer eine Hürde reisst, hat schon verloren —
 * ihn danach warten zu lassen, fügt der Erkenntnis nichts hinzu.
 */
export const FALL_TIME = 1.25

/** Wo ein Hindernis auftaucht und wo es entsorgt wird, m. */
export const SPAWN_AT = 24
const DESPAWN_AT = -6

/** Ab dieser Strecke kommen Riegel dazu, m. */
export const PIT_FROM = 140

/**
 * Wie weit die Erkennungszone über das Ding selbst hinausreicht, m.
 *
 * HIER STAND EIN EINZIGER WERT FÜR ALLES, UND DAS WAR FALSCH. 0,34 m Zugabe auf
 * jede Halbbreite macht aus einem 1,6 m breiten Ölfleck eine 2,28 m breite
 * Falle: der Läufer stürzte, während die Lache noch ein Drittel Meter vor ihm
 * lag. Man sieht es, weil die Lache gezeichnet wird — sie ist keine unsichtbare
 * Zone, sondern eine Scheibe mit dem Radius `HALF.oil`.
 *
 * Die Zugabe hat je Gegenstand einen anderen Grund, deshalb steht sie je
 * Gegenstand:
 *
 *   - Die HÜRDE ist ein Stab ohne Tiefe. Der Läufer hat eine, und er reisst die
 *     Stange mit dem Schienbein, nicht mit der Körpermitte.
 *   - ÖL und RIEGEL liegen am Boden und sind so gross, wie sie gezeichnet sind.
 *     Sie brauchen keine Zugabe — sie SIND die Zone.
 *   - Die ZELLE will getroffen werden. Ihre Zugabe ist ein Greifradius und
 *     macht das Spiel leichter, nicht schwerer.
 */
const REACH: Record<ItemKind, number> = {
  hurd: 0.18,
  oil: 0,
  pit: 0,
  cell: 0.34,
}

/** Der Anfangszustand. Auch der Zustand nach „Nochmal". */
export function dashStart(best = 0): DashState {
  return {
    running: true,
    /* Er läuft NICHT von allein los. Ein Läufer, der beim Öffnen schon rennt,
       nimmt einem die ersten Meter, bevor man die Hand an der Taste hat. */
    phase: 'ready',
    over: false,
    reason: '',
    dist: 0,
    v: 0,
    points: 0,
    best,
    cellPoints: 0,
    heat: 0,
    hot: false,
    battery: 100,
    y: 0,
    vy: 0,
    airborne: false,
    lives: LIVES,
    falling: 0,
    items: [],
    nextIn: 9,
    message: 'Los',
  }
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)

/** Die Hitzerate der Gangart. Was nicht geht, läuft und heizt nicht: 0. */
export function heatRate(gait: GaitKind): number {
  if (gait === 'sprint') return HEAT.sprint
  if (gait === 'run') return HEAT.run
  if (gait === 'walk') return HEAT.walk
  /* Stehen, Springen, Aufheben — im Spiel nicht vorgesehen, aber ein
     unbekannter Wert darf nicht heimlich heizen. */
  return 0
}

/**
 * Darf diese Gangart gerade gewählt werden?
 *
 * Die einzige Sperre ist die Übertemperatur. Sie steht hier und nicht in der
 * Oberfläche, damit ein Knopf nicht behaupten kann, was die Rechnung ablehnt.
 */
export function gaitAllowed(state: DashState, gait: GaitKind): boolean {
  return !(state.hot && gait !== 'walk')
}

/**
 * Welches Hindernis als nächstes kommt.
 *
 * Die Verteilung ist die der Vorlage. Riegel erscheinen erst ab 140 m — bis
 * dahin hat man Zeit, den Sprung zu lernen, den sie voraussetzen.
 */
export function pickItem(dist: number, r: number): ItemKind {
  if (r < 0.34) return 'hurd'
  if (r < 0.56) return 'oil'
  if (r < 0.74 && dist > PIT_FROM) return 'pit'
  if (r < 0.86) return 'cell'
  return 'hurd'
}

/**
 * Der Abstand zum nächsten Hindernis, m.
 *
 * Er schrumpft mit der Strecke und bleibt bei 5,2 m stehen. Das ist die einzige
 * Steigerung des Spiels: nicht schneller, sondern dichter. Schneller wäre keine
 * Steigerung, weil die Geschwindigkeit die des Roboters ist und der Roboter
 * nicht schneller wird.
 */
export function gapFor(dist: number, r: number): number {
  const gap = Math.max(5.2, 12.5 - dist / 110)
  return gap + r * gap * 0.5
}

/**
 * Ein Schritt.
 *
 * Gibt einen NEUEN Zustand zurück und ändert den alten nicht — dieselbe Regel
 * wie überall im Kern. Die Reihenfolge ist die der Ursachen: erst was die
 * Gangart macht (Strecke, Hitze, Akku), dann der Sprung, dann was daraus für
 * die Hindernisse folgt. Wer sie umstellt, prüft an einem Bild, das schon
 * weitergelaufen ist.
 */
export function dashStep(state: DashState, dt: number, world: DashWorld, input: DashInput): DashState {
  if (!state.running || state.over) return state

  /* Ein Bild, das lange ausgeblieben ist, darf keinen Sprung durch die Welt
     machen. Sonst steht man nach einem Tabwechsel plötzlich in einem Riegel. */
  const step = clamp(dt, 0, 0.08)

  const s: DashState = { ...state, items: state.items.map((i) => ({ ...i })), message: '' }

  /*
    AN DER STARTLINIE WARTET ER. Kein Vortrieb, kein Nachschub, kein Verbrauch
    — die Uhr steht, bis jemand losläuft. Ein Akku, der sich beim Zögern leert,
    bestraft das Nachdenken und sagt nichts über die Maschine.
  */
  if (s.phase === 'ready') {
    /* Er steht wirklich still. Ohne das behielte er die Geschwindigkeit vom
       Lauf vor dem Sturz und liefe im ersten Bild nach dem Start sofort mit
       vollem Tempo los. */
    s.v = 0
    if (!input.start && !input.jump) return s
    s.phase = 'run'
    s.message = 'Los'
    return s
  }

  /*
    SOLANGE ER LIEGT, LÄUFT NICHTS. Kein Vortrieb, kein Nachschub, keine
    Treffer — nur die Uhr des Sturzes. Würde die Welt weiterrollen, stünde er
    nach dem Aufstehen an einer Stelle, die er nie gelaufen ist.
  */
  if (s.phase === 'fall') {
    s.v = 0
    s.falling = Math.max(0, s.falling - step)
    /* Er liegt am Boden und ist eingeschaltet — der Akku läuft mit. */
    s.battery = drain(s.battery, world, step)
    if (s.battery <= 0) return finish(s, 'Akku leer')
    if (s.falling === 0) s.phase = 'prompt'
    return s
  }

  /* Die Frage steht — bis sie beantwortet ist, geschieht nichts. */
  if (s.phase === 'prompt') return s

  /* Die verlangte Geschwindigkeit wird angefahren, nicht angenommen. Der
     Schritt ist begrenzt, damit ein langes Bild nicht über das Ziel
     hinausschiesst — sonst pendelte die Geschwindigkeit um den Sollwert. */
  const want = Math.max(0, world.speed)
  const reach = ACCEL * step
  s.v = Math.abs(want - s.v) <= reach ? want : s.v + Math.sign(want - s.v) * reach
  const v = s.v
  s.dist += v * step

  /* --- Hitze ------------------------------------------------------------- */
  s.heat = clamp(s.heat + heatRate(world.gait) * step, 0, HEAT.limit)
  if (s.heat >= HEAT.limit && !s.hot) {
    s.hot = true
    s.message = 'Überhitzt · Zwangsgang'
  }
  if (s.hot && s.heat < HEAT.release) s.hot = false

  /* --- Akku --------------------------------------------------------------
     Der Verbrauch ist die echte mittlere Antriebsleistung des laufenden
     Gangbilds gegen den echten Akkuinhalt. Nichts davon ist für das Spiel
     erfunden; nur die Uhr läuft schneller. */
  s.battery = drain(s.battery, world, step)
  if (s.battery <= 0) return finish(s, 'Akku leer')

  /* --- Sprung ------------------------------------------------------------ */
  if (input.jump && !s.airborne) {
    s.airborne = true
    s.vy = JUMP.speed
  }
  if (s.airborne) {
    s.vy -= JUMP.gravity * step
    s.y += s.vy * step
    if (s.y <= 0) {
      s.y = 0
      s.vy = 0
      s.airborne = false
    }
  }


  /* --- Hindernisse -------------------------------------------------------- */
  s.nextIn -= v * step
  if (s.nextIn <= 0) {
    s.items.push({ kind: pickItem(s.dist, input.random()), x: SPAWN_AT, done: false })
    s.nextIn = gapFor(s.dist, input.random())
  }

  const kept: DashItem[] = []
  for (const item of s.items) {
    item.x -= v * step
    if (!item.done && Math.abs(item.x) < HALF[item.kind] + REACH[item.kind]) {
      const verdict = resolve(item.kind, s.y, v)
      if (verdict === 'hit') {
        item.done = true
        /* Der Sturz beendet diesen Schritt. Weiterzulaufen hiesse, im selben
           Bild noch am nächsten Hindernis vorbeizukommen. */
        return strike(s, reasonFor(item.kind))
      }
      if (verdict === 'collect') {
        item.done = true
        s.cellPoints += CELL_POINTS
        s.battery = Math.min(100, s.battery + CELL_CHARGE)
        s.message = `+${CELL_POINTS} · Akku +${CELL_CHARGE} %`
      }
    }
    if (item.x >= DESPAWN_AT) kept.push(item)
  }
  s.items = kept

  s.points = Math.round(s.dist) + s.cellPoints
  s.best = Math.max(s.best, s.points)
  return s
}

/** Was ein Hindernis bei dieser Höhe und dieser Geschwindigkeit auslöst. */
function resolve(kind: ItemKind, y: number, speed: number): 'hit' | 'collect' | 'pass' {
  if (kind === 'hurd') return y < CLEAR.hurd ? 'hit' : 'pass'
  if (kind === 'pit') return y < CLEAR.pit ? 'hit' : 'pass'
  if (kind === 'oil') return speed > OIL_SPEED && y < OIL_CLEAR ? 'hit' : 'pass'
  /* Die Zelle ist der einzige Gegenstand, den man treffen WILL. */
  return y > CLEAR.cell ? 'collect' : 'pass'
}

const reasonFor = (kind: ItemKind): string =>
  kind === 'hurd' ? 'Hürde gerissen' : kind === 'oil' ? 'Auf Öl ausgerutscht' : 'Riegel gerammt'

/** Ein Treffer. Kostet eine Hülle und macht kurz unverwundbar. */
/**
 * Er fällt.
 *
 * Die Strecke wird VORHER in den Bestwert gerettet: sie ist gelaufen worden,
 * auch wenn der Lauf gleich an der Startlinie neu beginnt. Ohne das wäre jeder
 * Sturz auch eine Löschung dessen, was man erreicht hat.
 */
function strike(s: DashState, reason: string): DashState {
  s.lives -= 1
  s.message = reason
  s.points = Math.round(s.dist) + s.cellPoints
  s.best = Math.max(s.best, s.points)
  s.y = 0
  s.vy = 0
  s.airborne = false
  if (s.lives <= 0) return finish(s, reason)
  s.phase = 'fall'
  s.falling = FALL_TIME
  return s
}

/**
 * Aufstehen an der Startlinie — der nächste Versuch.
 *
 * WIRD VON AUSSEN AUFGERUFEN und nicht vom Schritt. Nach dem Sturz steht die
 * Frage „neu starten oder beenden"; erst die Antwort bringt ihn wieder hoch.
 * Automatisch weiterzulaufen nähme dem Sturz seine Folge.
 *
 * WAS ZURÜCKGESETZT WIRD und was nicht, ist die eigentliche Aussage. Strecke,
 * Hindernisse und Hitze fangen von vorn an — er steht wieder am Anfang und ist
 * abgekühlt. Der AKKU nicht: ein Sturz lädt kein Pack. Damit haben die drei
 * Versuche einen Bogen, statt dreimal derselbe Versuch zu sein.
 */
export function dashRetry(state: DashState): DashState {
  const s: DashState = { ...state, items: [] }
  s.phase = 'ready'
  s.falling = 0
  s.y = 0
  s.vy = 0
  s.airborne = false
  s.dist = 0
  s.v = 0
  s.cellPoints = 0
  s.points = 0
  s.heat = 0
  s.hot = false
  s.items = []
  s.nextIn = 9
  s.message = 'An der Startlinie'
  return s
}

/** Was der Akku in dieser Zeitspanne verliert, Prozentpunkte. */
function drain(battery: number, world: DashWorld, step: number): number {
  if (world.capacity <= 0) return battery
  return clamp(battery - (100 * world.power) / world.capacity * TIME_LAPSE * step, 0, 100)
}

function finish(s: DashState, reason: string): DashState {
  s.over = true
  s.phase = 'prompt'
  s.reason = reason
  s.falling = 0
  s.y = 0
  s.vy = 0
  s.airborne = false
  s.points = Math.round(s.dist) + s.cellPoints
  s.best = Math.max(s.best, s.points)
  return s
}

/**
 * Der Akkuinhalt, J.
 *
 * Ausgelegt wie in der Stückliste: mittlere Antriebsleistung beim GEHEN mal
 * drei Stunden. Bewusst das Gehen und nicht die gerade laufende Gangart — ein
 * Pack, dessen Inhalt sich beim Sprint mitvergrössert, wäre keine Auslegung.
 */
export const packCapacity = (walkPower: number, hours = 3): number => walkPower * hours * 3600
